import { Box, CircularProgress, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFileSignedUrl } from "@/api/services/global/file";
import { ImagePreviewModal } from "@/components/commons/ImagePreviewModal";
import { SIGNED_URL_STALE_TIME } from "@/constants/file";
import { black, background } from "@/theme/colors";

type TFileImageProps = {
  /** S3 file path — resolved to a signed URL via `useFileSignedUrl` */
  filePath: string;
  /** Tap the loaded image to open a full-screen pinch-zoom preview (default on; pass `false` to opt out) */
  zoomable?: boolean;
};

/**
 * Shared image frame for S3-stored content images (questions, vocabulary cards, flashcards, mock exams).
 * Resolves the signed URL itself and shows a loading / error placeholder inside a fixed-size frame.
 */
export const FileImage: FC<TFileImageProps> = ({ filePath, zoomable = true }) => {
  const { t } = useTranslation();
  const { data: signedUrlData, isLoading, isError } = useFileSignedUrl(filePath, { staleTime: SIGNED_URL_STALE_TIME });
  const imageUrl = signedUrlData?.data?.signedUrl;
  const [imgLoadError, setImgLoadError] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const showBorder = isLoading || isError || imgLoadError || !imageUrl;

  useEffect(() => {
    setImgLoadError(false);
  }, [filePath, imageUrl]);

  return (
    <Box
      sx={{
        mt: 1.5,
        width: "100%",
        maxWidth: 288,
        minHeight: 120,
        border: showBorder ? `1px dashed ${black[90]}` : undefined,
        bgcolor: background.white,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        alignSelf: "center",
      }}
    >
      {isLoading ? (
        <CircularProgress size={24} />
      ) : isError || imgLoadError || !imageUrl ? (
        <Typography variant="body14">{t("common.imageLoadError")}</Typography>
      ) : (
        <Box
          component="img"
          src={imageUrl}
          // This app targets Japanese language learners on mobile — not optimized for screen readers.
          // Using empty alt for decorative content images is acceptable in this context.
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setImgLoadError(true)}
          onClick={zoomable ? () => setIsPreviewOpen(true) : undefined}
          sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", cursor: zoomable ? "pointer" : undefined }}
        />
      )}
      {zoomable && imageUrl && <ImagePreviewModal open={isPreviewOpen} imageUrl={imageUrl} onClose={() => setIsPreviewOpen(false)} />}
    </Box>
  );
};
